// ** React Imports
import { useState, useEffect, useContext } from "react";

// ** Third Party Components
import { useTranslation } from "react-i18next";
import styled from "styled-components";

// ** Custom Components
import Block from "components/layout/Block";
import Text from "components/typography/Text";
import Attachment from "./Attachment";
import TimePresenter from "components/other/TimePresenter";
import ReadedStatusIcon from "components/icons/ReadedStatusIcon";

// ** Utils & Helpers
import convertText from "utils/convertText";

// ** Context
import { ConversationContext } from "../../../helpers/ConversationContext";

// ** Styled Components
import { StyledMessage, StyledAcceptVideoChatButton } from "../styles";
import { StyledOverlay } from "styles/components/Overlay.styled";

export const StyledWrapper = styled(Block)`
	background-color: ${({ theme: { isDarkMode } }) =>
		isDarkMode ? "#424242" : "#d0d0d0"};
`;

const MessageText = ({
	item: {
		id,
		read,
		message,
		date,
		file_id,
		file_small,
		file_big,
		attachmentRemoving,
		attachmentIsRemoved,
		sending,
		additional_status,
	} = {},
	isLast,
	isRightSide,
	onAttachmentClick,
	onAttachmentRemove,
	IdLastReadedMessageInRightSide,
	IdLastVideoChatInfoInLeftSide,
	IdLastVideoChatInfoInRightSide,
	conversationUserAvatar,
	nextMinute,
	onAcceptVideoChat,
}) => {
	const { t } = useTranslation(["userpanel"]);
	const conversationContext = useContext(ConversationContext);

	const [showBoldedMessage, setShowBoldedMessage] = useState(
		!read && !isRightSide
	);

	const addMargin =
		message &&
		message[0] &&
		((typeof message[0] === "string" && message[0].trim().length > 0) ||
			typeof message[0] === "object");

	const handleOnClickAttachment = (event) => {
		if (typeof onAttachmentClick === "function") {
			onAttachmentClick(event, {
				id,
				file_id,
				file_big,
			});
		}
	};

	const handleOnRemoveAttachment = () => {
		if (typeof onAttachmentRemove === "function") {
			onAttachmentRemove(id);
		}
	};

	useEffect(() => {
		let timer1 = setTimeout(() => {
			setShowBoldedMessage(false);
		}, 3 * 1000);
		return () => {
			clearTimeout(timer1);
		};
	}, []);

	return (
		<Block flex column alignEnd={isRightSide} alignStart={!isRightSide}>
			<Block flex alignEnd>
				<StyledMessage
					isRightSide={isRightSide}
					haveSomethingBottom={date && nextMinute}
				>
					<Block textBreak bold={showBoldedMessage}>
						{convertText({
							pieces: message,
						})}
					</Block>
					{attachmentIsRemoved === true || additional_status === 3 ? (
						<StyledWrapper
							small
							color="grey"
							width="120px"
							height="120px"
							flex
							alignCenter
							justifyCenter
							style={{
								marginTop: addMargin ? "0.5rem" : null,
								marginBottom:
									date && nextMinute ? "0.25rem" : null,
							}}
						>
							<Text
								style={{
									textAlign: "center",
									padding: "0.5rem",
								}}
							>
								{t("userpanel:messages.userRemovedPhoto")}
							</Text>
						</StyledWrapper>
					) : null}
					{additional_status === 1 &&
					IdLastVideoChatInfoInLeftSide === id &&
					conversationContext.showAcceptVideoChatButton &&
					!isRightSide ? (
						<Block flex column>
							<Text color="grey" small>
								{t("userpanel:messages.userCameraOn")}
							</Text>
							<Block flex column width="120px">
								<Block relative>
									{conversationContext.chatPartnerStreamPicture ? (
										<img
											style={{
												width: "100%",
												marginTop: "0.5rem",
											}}
											src={
												conversationContext.chatPartnerStreamPicture
											}
										/>
									) : null}
									<StyledOverlay
										zIndex={3}
										style={{ cursor: "pointer" }}
										onClick={
											!conversationContext.isOpenVideoChat
												? onAcceptVideoChat
												: null
										}
									/>
								</Block>
								<StyledAcceptVideoChatButton
									label={t("userpanel:messages.look")}
									onClick={
										!conversationContext.isOpenVideoChat
											? onAcceptVideoChat
											: null
									}
								/>
							</Block>
						</Block>
					) : additional_status === 1 &&
					  IdLastVideoChatInfoInRightSide === id &&
					  (conversationContext.secondDevicePublisherPicture ||
							conversationContext.publisherPicture) &&
					  isRightSide ? (
						<Block flex column>
							<Text color="grey" small>
								{t("userpanel:messages.userCameraOn")}
							</Text>
							<Block relative>
								<img
									style={{
										width: "120px",
										margin: "0.5rem 0 0.25rem 0",
									}}
									src={
										conversationContext.publisherPicture ||
										conversationContext.secondDevicePublisherPicture
									}
								/>
								<StyledOverlay zIndex={3} />
							</Block>
						</Block>
					) : additional_status === 1 ? (
						<Block flex alignCenter small color="grey">
							<Text color="grey" small>
								{t("userpanel:messages.userCameraOn")}
							</Text>
						</Block>
					) : null}
					{file_small &&
					file_small.length > 0 &&
					!attachmentRemoving ? (
						<Attachment
							preview={file_small}
							onClick={handleOnClickAttachment}
							onRemove={
								isRightSide ? handleOnRemoveAttachment : null
							}
							sending={sending}
							customMarginTop={addMargin ? "0.5rem" : null}
							isRightSide={isRightSide}
							nextMinute={nextMinute}
						/>
					) : null}
					{date && nextMinute && (
						<Block flex justifyEnd color="grey" mt={1}>
							<TimePresenter
								date={date}
								style={{
									lineHeight: "0.7rem",
									fontSize: "0.7rem",
								}}
							/>
						</Block>
					)}
				</StyledMessage>
			</Block>
			{IdLastReadedMessageInRightSide === id &&
				!isLast &&
				isRightSide && (
					<ReadedStatusIcon
						srcAvatar={conversationUserAvatar}
						customMargin={"0.25rem 0"}
					/>
				)}
		</Block>
	);
};

export default MessageText;
